/*
 * Hover a person, see the person (X-A3 A3).
 *
 * A card that appears over a face or a name after a short dwell is the most-used lookup there is in
 * a chat product, and it is the one interaction that must never get in the way: it waits 300 ms so
 * that a pointer crossing the row opens nothing (the same rule as the resize handles, UX §3), it
 * takes no focus, it closes when the pointer leaves either the anchor or the card, and Escape closes
 * it from the keyboard.
 *
 * It exists only for the pointer. There is no touch gesture for "hover", and opening a card on a tap
 * would take the tap away from the control under it; there is no keyboard gesture either, because a
 * card that popped up while tabbing through a list of names would be noise. Both of those readers
 * get the whole record instead: the same name, clicked, opens the profile in the details column.
 * Nothing is in the card that is not also there — which is why the card is not in the accessible
 * tree's way at all.
 *
 * What is left in this file is the dwell itself: the timer, the pointer kind and the key. The panel is
 * the control layer's `ui/Popover` in its `dwell` mode (B4-12) — an anchor instead of a trigger, no
 * focus taken, the pointer handlers on the panel as well — which is what this file used to reach past
 * that wrapper into Radix for.
 */

import { useEffect, useRef, useState, type ReactNode } from "react"
import { Popover } from "../../ui/index.ts"
import "./people.css"

/** The dwell before the card appears. A pointer merely crossing a row must open nothing. */
export const HOVER_CARD_DELAY_MS = 300

/**
 * How long the card survives the pointer leaving it. It is the width of the gap between the anchor
 * and the card: without it, travelling into the card closes it.
 */
const CLOSE_GRACE_MS = 120

/**
 * Whether this reader points at things. A coarse pointer — a finger — has no hover state, and a
 * browser that cannot answer is treated as one: drawing no card is always safe.
 */
function hoverCapable(): boolean {
	return globalThis.matchMedia?.("(hover: hover)")?.matches === true
}

export function HoverCard({
	children,
	card,
	label,
}: {
	/** The face or the name the card hangs off. */
	children: ReactNode
	/** What the card shows; built only while it is open. */
	card: ReactNode
	/** The card's accessible name, already translated. */
	label: string
}) {
	const [open, setOpen] = useState(false)
	// One timer for both directions: a pointer that leaves and comes back must not open the card
	// twice, and a pending open must die with the pointer that asked for it.
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

	function stopTimer() {
		if (timer.current !== null) clearTimeout(timer.current)
		timer.current = null
	}

	function later(what: () => void, ms: number) {
		stopTimer()
		timer.current = setTimeout(() => {
			timer.current = null
			what()
		}, ms)
	}

	useEffect(() => stopTimer, [])

	// Escape belongs to the pointer's card as much as to a dialog: the pointer is somewhere over the
	// row, and the key is the way out that does not require moving it.
	useEffect(() => {
		if (!open) return undefined
		function onKey(event: KeyboardEvent) {
			if (event.key === "Escape") setOpen(false)
		}
		document.addEventListener("keydown", onKey)
		return () => document.removeEventListener("keydown", onKey)
	}, [open])

	return (
		<Popover
			label={label}
			side="top"
			align="start"
			className="tb-hovercard"
			open={open}
			onOpenChange={(next) => {
				// The card opens on a dwell and on nothing else; the panel may only ever close it —
				// which is what an outside click and a click on the anchor itself do.
				if (!next) {
					stopTimer()
					setOpen(false)
				}
			}}
			dwell={{
				// The pointer has arrived in the card: whatever close was pending is cancelled, so the
				// gap between the anchor and the card is not a trap.
				onPointerEnter: stopTimer,
				onPointerLeave: () => later(() => setOpen(false), CLOSE_GRACE_MS),
			}}
			trigger={
				<span
					className="tb-hover-anchor"
					onPointerEnter={(event) => {
						if (event.pointerType === "touch" || !hoverCapable()) return
						later(() => setOpen(true), HOVER_CARD_DELAY_MS)
					}}
					onPointerLeave={() => later(() => setOpen(false), CLOSE_GRACE_MS)}
				>
					{children}
				</span>
			}
		>
			{card}
		</Popover>
	)
}
